"use client"

import { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel"
import { Maximize, X } from "lucide-react"
import { ImageWithFallback } from "@/components/ui/ImageWithFallback"
import { motion, AnimatePresence } from "framer-motion"

interface ProjectImageGalleryProps {
    images: string[]
    projectTitle: string
}

export function ProjectImageGallery({ images, projectTitle }: ProjectImageGalleryProps) {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const carouselContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (isModalOpen && carouselContainerRef.current) {
            // Set height on carousel content wrapper and inner flex container
            const setHeights = () => {
                const carouselContent = carouselContainerRef.current?.querySelector('[data-slot="carousel-content"]') as HTMLElement
                if (carouselContent) {
                    carouselContent.style.height = '100%'
                    const innerFlex = carouselContent.querySelector('div') as HTMLElement
                    if (innerFlex) {
                        innerFlex.style.height = '100%'
                    }
                }
            }
            setHeights()
            // Retry after a short delay to ensure DOM is ready
            const timeoutId = setTimeout(setHeights, 50)
            return () => clearTimeout(timeoutId)
        }
    }, [isModalOpen])

    return (
        <>
            <Card className="px-0 py-10 md:py-8 relative">
                <Button
                    variant={"ghost"}
                    className="absolute top-4 right-4 z-50 hover:bg-primary! hover:text-white!"
                    onClick={() => setIsModalOpen(true)}
                    aria-label="Maximize images"
                >
                    <Maximize className="size-4" />
                    <span className="sr-only">Maximize</span>
                </Button>
                <CardContent className="px-0">
                    <Carousel className="w-full flex flex-col gap-10 md:gap-8">
                        <CarouselContent>
                            {images.map((img, index) => (
                                <CarouselItem key={`${img}-${index}`}>
                                    <div className="relative w-full aspect-square md:aspect-[4/3] overflow-hidden">
                                        <ImageWithFallback
                                            src={img}
                                            alt={`${projectTitle} Image ${index + 1}`}
                                            title={`${projectTitle} Image ${index + 1}`}
                                            fill
                                            fetchPriority="high"
                                            loading="lazy"
                                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                            className="object-contain object-center"
                                        />
                                    </div>
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                        <div className="flex items-center justify-between gap-4 px-4">
                            <CarouselPrevious aria-label="Previous image" />
                            <CarouselNext aria-label="Next image" />
                        </div>
                    </Carousel>
                </CardContent>
            </Card>

            {/* Image Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm"
                            onClick={() => setIsModalOpen(false)}
                        />

                        {/* Modal */}
                        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 30
                                }}
                                className="relative w-full max-w-7xl h-[90vh] flex flex-col"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Close Button */}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setIsModalOpen(false)}
                                    className="absolute top-4 right-4 z-50 bg-background/80 hover:bg-background"
                                    aria-label="Close modal"
                                >
                                    <X className="h-5 w-5" />
                                    <span className="sr-only">Close</span>
                                </Button>

                                {/* Carousel Container */}
                                <div className="w-full h-full flex flex-col rounded-lg overflow-hidden relative">
                                    <Carousel className="w-full h-full flex flex-col" opts={{ align: "start", loop: true }}>
                                        <div ref={carouselContainerRef} className="flex-1 min-h-0 h-full">
                                            <CarouselContent className="h-full [&>div]:h-full border-none">
                                                {images.map((img, index) => (
                                                    <CarouselItem key={`modal-${img}-${index}`} className="basis-full h-full flex">
                                                        <div className="w-full h-full overflow-y-auto overflow-x-hidden flex-1 flex flex-col">
                                                            <div className="w-full flex items-start justify-center m-auto p-4">
                                                                <img
                                                                    src={img}
                                                                    alt={`${projectTitle} Image ${index + 1}`}
                                                                    className="w-full h-auto object-contain"
                                                                />
                                                            </div>
                                                        </div>
                                                    </CarouselItem>
                                                ))}
                                            </CarouselContent>
                                        </div>
                                        <div className="flex items-center justify-between gap-4 px-4 py-4 shrink-0 h-20 absolute top-[50%] left-0 right-0 translate-y-[-50%]">
                                            <CarouselPrevious
                                                aria-label="Previous image"
                                                className="bg-background/80 hover:bg-background"
                                            />
                                            <CarouselNext
                                                aria-label="Next image"
                                                className="bg-background/80 hover:bg-background"
                                            />
                                        </div>
                                    </Carousel>
                                </div>
                            </motion.div>
                        </div>
                    </>
                )}
            </AnimatePresence>
        </>
    )
}

